import { Box, Divider, Typography } from '@mui/material';
import type { ReactNode } from 'react';

const codeSx = {
  display: 'block',
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: '0.78rem',
  lineHeight: 1.45,
  bgcolor: 'grey.100',
  border: '1px solid',
  borderColor: 'grey.300',
  borderRadius: 1,
  p: 1.5,
  my: 1,
  overflowX: 'auto',
  whiteSpace: 'pre-wrap' as const,
};

const SectionTitle = ({ children }: { children: ReactNode }) => (
  <Typography
    variant="subtitle1"
    sx={{
      fontWeight: 700,
      mt: 2,
      mb: 0.75
    }}>
    {children}
  </Typography>
);

/**
 * Hermész HTML applet authoring docs shown in the CMS create/edit súgó.
 */
const HtmlAppletHelperDocs = () => {
  return (
    <Box sx={{ maxWidth: 920 }}>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          marginBottom: "16px"
        }}>
        Az applet egy <strong>mini-app</strong>: HTML/CSS/JS fut a Hermész
        WebView-ban. A host injektálja a <code>window.Hermesz</code> API-t és a
        aktuális (vagy default) témát. Az eredmények a Hermészbe a bridge-en
        keresztül mentődnek — soha ne ágyazz be API kulcsot a HTML-be.
      </Typography>
      <SectionTitle>Felelősség és szabályok</SectionTitle>
      <Typography component="div" variant="body2">
        <ul style={{ marginTop: 0, paddingLeft: 20 }}>
          <li>
            A létrehozó / szerkesztő felel a tartalomért, jogszerűségért és
            adatkezelésért.
          </li>
          <li>
            Tilos: rosszindulatú kód, phishing, jogosulatlan adatgyűjtés,
            titkok / API kulcsok beágyazása.
          </li>
          <li>
            A feltöltést Content Safety ellenőrzi; max. HTML ~2&nbsp;MB, ZIP
            ~15&nbsp;MB.
          </li>
          <li>
            ZIP: kötelező <code>index.html</code> a gyökérben; engedélyezett
            kiterjesztések: html, css, js, json, képek, fontok.
          </li>
        </ul>
      </Typography>
      <Divider sx={{ my: 1.5 }} />
      <SectionTitle>Hermész Bridge API</SectionTitle>
      <Typography variant="body2" sx={{
        marginBottom: "16px"
      }}>
        A host a betöltés után elérhetővé teszi a{' '}
        <code>window.Hermesz</code> objektumot. Promise-alapú metódusok:
      </Typography>
      <Box component="pre" sx={codeSx}>{`// Eredmény mentése (max. ~256 KB JSON string)
const outcome = await Hermesz.saveResult(JSON.stringify({
  answers: [...],
  submittedAt: new Date().toISOString()
}));
// outcome: { ok: true, queued: false|true }
// queued === true → offline mentve, online-ra sync

// Kontextus (appletId, platform, locale, theme)
const ctx = await Hermesz.getContext();

// Csak a téma
const theme = await Hermesz.getTheme();
// vagy szinkron: Hermesz.theme

// WebView bezárása
await Hermesz.close();`}</Box>
      <Typography
        variant="body2"
        sx={{
          color: "text.secondary",
          marginBottom: "16px"
        }}>
        Offline <code>saveResult</code> lokális sorba kerül, és amikor a
        készülék újra online, a Hermész automatikusan elküldi. A Promise
        sikeres marad (<code>queued: true</code>).
      </Typography>
      <SectionTitle>Téma (remote vagy default)</SectionTitle>
      <Typography variant="body2" sx={{
        marginBottom: "16px"
      }}>
        Betöltéskor a host beállítja a CSS változókat és a{' '}
        <code>Hermesz.theme</code> objektumot. Nincs aktív remote téma → Hermész
        default (<code>#1A2543</code>). Figyelhető a{' '}
        <code>hermesz:theme</code> esemény is (a bridge későn injectálódhat).
      </Typography>
      <Box component="pre" sx={codeSx}>{`/* CSS — ajánlott használat */
:root {
  --brand: var(--hermesz-primary, #1A2543);
  --brand-press: var(--hermesz-secondary, #1A2543);
  --on-brand: var(--hermesz-on-primary, #ffffff);
}

.btn { background: var(--brand); color: var(--on-brand); }`}</Box>
      <Box component="pre" sx={codeSx}>{`// Injektált CSS változók
--hermesz-primary
--hermesz-secondary
--hermesz-icon-primary
--hermesz-icon-secondary
--hermesz-illustration
--hermesz-on-primary
--hermesz-on-secondary
--brand / --brand-press / --on-brand  (aliasok)

// Hermesz.theme / getTheme() mezők
{
  source: "remote" | "default",
  primary, secondary, iconPrimary, iconSecondary,
  illustrationPrimary, onPrimary, onSecondary,
  logoUrl, backgroundImageUrl
}`}</Box>
      <Box component="pre" sx={codeSx}>{`window.addEventListener('hermesz:theme', (e) => {
  applyTheme(e.detail); // saját CSS / logo frissítés
});`}</Box>
      <SectionTitle>Külső API / fetch</SectionTitle>
      <Typography component="div" variant="body2">
        <ul style={{ marginTop: 0, paddingLeft: 20 }}>
          <li>
            HTTPS <code>fetch</code> / XHR a WebView-ban engedélyezett (saját
            backendhez).
          </li>
          <li>
            Hermész saját API-hoz <strong>ne</strong> használd a raw API
            kulcsot — csak a bridge (<code>saveResult</code>).
          </li>
        </ul>
      </Typography>
      <SectionTitle>Minimális példa</SectionTitle>
      <Box component="pre" sx={codeSx}>{`<!DOCTYPE html>
<html lang="hu">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <style>
    :root { --brand: var(--hermesz-primary, #1A2543); }
    button { background: var(--brand); color: var(--hermesz-on-primary, #fff); }
  </style>
</head>
<body>
  <h1>Helló</h1>
  <button id="send">Küldés</button>
  <script>
    document.getElementById('send').onclick = async () => {
      if (!window.Hermesz) return alert('Preview: nincs bridge');
      await Hermesz.saveResult(JSON.stringify({ ok: true }));
      await Hermesz.close();
    };
  </script>
</body>
</html>`}</Box>
    </Box>
  );
};

export default HtmlAppletHelperDocs;
